import { useEffect } from "react";
import { Outlet, useParams } from "react-router";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import { BreadcrumbsContextApi } from "components/Breadcrumbs/Context";
import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import useTypedContext from "hooks/useTypedContext";

import { SCANS_FILTERS_ORDER_KEY } from "../components/Table/constants";
import { ScanResourcesOutletContext } from "../ScanResources/types";
import ScanDetailHeader from "./components/Header";
import { GET_SCAN_DETAIL } from "./gql";

const ScanDetailLayout = () => {
  const { scanId = "", runId } = useParams<{ scanId: string; runId?: string }>();
  const isScanRunPage = !!runId;
  const { setItems, resetItems } = useTypedContext(BreadcrumbsContextApi);

  const { data, loading, error } = usePolledQuery(GET_SCAN_DETAIL, {
    variables: { id: scanId },
    pollingGroup: PollingIntervalGroups.Lists,
    // Nested scan-run routes load their own data; don't gate them on the schedule query.
    skip: !scanId || isScanRunPage,
  });

  const scan = data?.scan;

  useEffect(() => {
    if (isScanRunPage) {
      return;
    }

    setItems([
      {
        title: "Resources",
        link: "/resources/iac-management",
      },
      {
        title: "Cloud scanning",
        link: getBreadcrumbsBackUrl("/resources/scans", SCANS_FILTERS_ORDER_KEY),
      },
      {
        title: scan?.name || "",
      },
    ]);

    return () => {
      resetItems();
    };
  }, [scanId, scan?.name, isScanRunPage, setItems, resetItems]);

  const ErrorContent = useErrorHandle(isScanRunPage ? undefined : error);

  if (isScanRunPage) {
    return <Outlet />;
  }

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !scan) {
    return <PageLoading />;
  }

  if (!scan) {
    return <NotFoundPage />;
  }

  const outletContext: ScanResourcesOutletContext = { run: scan.latestRun ?? undefined, scan };

  return (
    <>
      <ScanDetailHeader scan={scan} />
      <Outlet context={outletContext} />
    </>
  );
};

export default ScanDetailLayout;
